import { useCallback, useMemo } from 'react';
import { useTranslation, UseTranslationOptions } from 'react-i18next';

import { appConfigs } from '@/constants/tenant-configs';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';

import { SupportedLanguages } from '@/i18n/i18n';

import { setTenant } from '@/redux/configs/actions';
import { selectConfigsState } from '@/redux/configs/selectors';

import { Tenant } from '@/types/common';
import { Language, NameSpaceType } from '@/types/i18n';

import {
  getCountryTranslations as getCountryTranslationsUtil,
  TranslationCountries,
} from '@/utils/countryTranslations';
import { getLanguageFromAvailable } from '@/utils/languages';
import { ensureTenantTranslations } from '@/utils/tenantTranslationLoader';
import { isSupportedLanguage } from '@/utils/translations';

export function useTranslate(
  ns?: NameSpaceType,
  options?: UseTranslationOptions<string>,
) {
  const dispatch = useAppDispatch();
  const { t, i18n, ...translation } = useTranslation(ns, options);
  const { language } = i18n;
  const { tenant } = useAppSelector(selectConfigsState);

  if (!isSupportedLanguage(language)) {
    throw new Error(`Unsupported or missing language: ${language}`);
  }

  const translate = useCallback(
    (translationKey: string, options?: Record<string, unknown>) => {
      if (!translationKey) {
        return '';
      }
      return t(translationKey, options);
    },
    [t],
  );

  const translateIfExists = useCallback(
    (
      translationKey?: string,
      options?: Record<string, unknown>,
    ): string | undefined => {
      if (!translationKey) {
        return '';
      }
      const isExist = i18n.exists(translationKey, options);
      if (isExist) {
        return t(translationKey, options);
      } else if (options?.defaultValue) {
        return options.defaultValue as string;
      }
      return '';
    },
    [i18n, t],
  );

  const getCountryTranslations = useCallback(
    () => getCountryTranslationsUtil(Language.English),
    [],
  );

  const { country, nationality, policyByCountry }: TranslationCountries =
    useMemo(() => {
      const trans = getCountryTranslations();
      return {
        country: { ...trans.country, 'VGB-shorten': 'BVI' },
        policyByCountry: {
          ...trans.country,
          'VGB-shorten': 'BVI',
          CYM: 'Cayman National',
        },
        nationality: trans.nationality,
      };
    }, [getCountryTranslations]);

  const switchTenant = useCallback(
    async (tenantToUpdate: Tenant) => {
      dispatch(
        setTenant({
          tenant: tenantToUpdate,
        }),
      );
      const availableLanguages = appConfigs[tenantToUpdate]
        ?.availableLanguages ?? [Language.English];
      const loadLanguage = getLanguageFromAvailable(
        availableLanguages,
        isSupportedLanguage(i18n.language) ? i18n.language : null,
      );

      if (SupportedLanguages.includes(loadLanguage)) {
        await ensureTenantTranslations(tenantToUpdate, loadLanguage, i18n);
      }
    },
    [dispatch, i18n],
  );

  const switchLanguage = useCallback(
    async (newLanguage: Language, tenantForTranslations?: Tenant) => {
      if (!SupportedLanguages.includes(newLanguage)) {
        return;
      }
      const activeTenant = tenantForTranslations ?? tenant;
      if (activeTenant) {
        await ensureTenantTranslations(activeTenant, newLanguage, i18n);
      }
      if (i18n.language !== newLanguage) {
        await i18n.changeLanguage(newLanguage);
      }
    },
    [i18n, tenant],
  );

  return {
    t: translate,
    translateIfExists,
    switchTenant,
    switchLanguage,
    policyByCountry,
    i18n,
    country,
    nationality,
    language,
    ...translation,
  };
}
